<template>
    <div class="container">

        <!-- HEADER -->
        <div class="privacy-header">
            <h1>Privacy Policy</h1>
            <p>Last updated: March 03, 2025</p>
            <p>
                This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
            </p>
            <p>
                We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy. This Privacy Policy has been created with the help of the 
                <a href="https://www.privacypolicies.com/privacy-policy-generator/" target="_blank">Privacy Policy Generator</a>.
            </p>
        </div>

        <!-- INTERPRETATION & DEFINITIONS -->
        <div class="interpretation-definitions">
            <h2>Interpretation and Definitions</h2>
            <h3>Interpretation</h3>
            <p>
                Words with initial capital letters have meanings defined under the following conditions. The definitions shall have the same meaning regardless of singular or plural usage.
            </p>

            <h3>Definitions</h3>
            <p>For the purposes of this Privacy Policy:</p>
            <ul>
                <li><strong>Account</strong>: a unique account created for You to access our Service or parts of our Service.</li>
                <li><strong>Affiliate</strong>: an entity that controls, is controlled by, or is under common control with a party (ownership of 50%+ of shares/equity).</li>
                <li><strong>Company</strong>: Galleria Stefano Forni, Via Garibaldi, 7 - Bologna (BO) 40124.</li>
                <li><strong>Cookies</strong>: small files placed on Your device containing details of Your browsing history.</li>
                <li><strong>Country</strong>: Italy</li>
                <li><strong>Device</strong>: any device that can access the Service (computer, cellphone, tablet).</li>
                <li><strong>Personal Data</strong>: any information relating to an identified or identifiable individual.</li>
                <li><strong>Service</strong>: refers to the Website.</li>
                <li><strong>Service Provider</strong>: persons processing data on behalf of the Company.</li>
                <li><strong>Usage Data</strong>: data collected automatically via the Service or its infrastructure.</li>
                <li><strong>Website</strong>: <a href="http://www.galleriastefanoforni.com" target="_blank" rel="external nofollow noopener">www.galleriastefanoforni.com</a></li>
                <li><strong>You</strong>: the individual or entity accessing or using the Service.</li>
            </ul>
        </div>

        <!-- COLLECTING & USING DATA -->
        <div class="data-collection">
            <h2>Collecting and Using Your Personal Data</h2>
            
            <!-- Types of Data Collected -->
            <div class="types-of-data">
                <h3>Types of Data Collected</h3>
                
                <h4>Personal Data</h4>
                <p>
                    While using Our Service, We may ask You to provide personally identifiable information, including:
                </p>
                <ul>
                    <li>Email address</li>
                    <li>First name and last name</li>
                    <li>Usage Data</li>
                </ul>

                <h4>Usage Data</h4>
                <p>Usage Data is collected automatically when using the Service and may include IP address, browser type, pages visited, time spent, unique device identifiers, and other diagnostic data.</p>
                <p>
                    On mobile devices, We may also collect device type, unique ID, IP address, operating system, browser type, and other diagnostic data.
                </p>
            </div>

            <!-- Cookies -->
            <div class="cookies">
                <h4>Tracking Technologies and Cookies</h4>
                <p>
                    We use Cookies and similar tracking technologies (beacons, tags, scripts) to monitor activity on Our Service and store information.
                </p>
                <ul>
                    <li><strong>Cookies:</strong> Small files on Your device. You can refuse cookies via browser settings, but some Service parts may not function.</li>
                    <li><strong>Web Beacons:</strong> Small electronic files in certain sections or emails to count users, analyze statistics, and verify system integrity.</li>
                </ul>
                <p>
                    Cookies can be "Persistent" (remain after browser is closed) or "Session" (deleted when browser is closed). Learn more on the 
                    <a href="https://www.privacypolicies.com/blog/privacy-policy-template/#Use_Of_Cookies_Log_Files_And_Tracking" target="_blank">Privacy Policies website</a>.
                </p>
            </div>
        </div>

        <!-- USE OF PERSONAL DATA -->
        <div class="use-of-personal-data">
            <h3>Use of Your Personal Data</h3>
            <ul>
                <li>Provide and maintain the Service</li>
                <li>Manage Your Account</li>
                <li>Performance of a contract</li>
                <li>Contact You via email, SMS, or push notifications</li>
                <li>Provide news, offers, and similar information</li>
                <li>Manage Your requests</li>
                <li>Business transfers (mergers, acquisitions, asset sales)</li>
                <li>Other purposes such as data analysis and service improvement</li>
            </ul>
        </div>

        <!-- RETENTION & TRANSFER -->
        <div class="retention-transfer">
            <h3>Retention of Your Personal Data</h3>
            <p>We retain Personal Data only as long as necessary for legal obligations, disputes, or service improvement. Usage Data is generally retained for a shorter period.</p>

            <h3>Transfer of Your Personal Data</h3>
            <p>Your data may be processed and stored outside your jurisdiction. We take steps to ensure data security during transfer.</p>
        </div>

        <!-- DELETE DATA -->
        <div class="delete-data">
            <h3>Delete Your Personal Data</h3>
            <p>You can request deletion or update of Your Personal Data via Your account settings or by contacting Us.</p>
        </div>

        <!-- DISCLOSURE -->
        <div class="disclosure">
            <h3>Disclosure of Your Personal Data</h3>
            
            <h4>Business Transactions</h4>
            <p>Personal Data may be transferred in the event of mergers, acquisitions, or asset sales.</p>

            <h4>Law Enforcement</h4>
            <p>We may disclose Your Personal Data if required by law or public authorities.</p>

            <h4>Other Legal Requirements</h4>
            <ul>
                <li>Comply with a legal obligation</li>
                <li>Protect and defend the rights or property of the Company</li>
                <li>Prevent or investigate wrongdoing in connection with the Service</li>
                <li>Protect personal safety of Users or the public</li>
                <li>Protect against legal liability</li>
            </ul>
        </div>

        <!-- SECURITY -->
        <div class="security">
            <h3>Security of Your Personal Data</h3>
            <p>We use commercially acceptable means to protect Your Personal Data but cannot guarantee absolute security.</p>
        </div>

        <!-- CHILDREN -->
        <div class="children-privacy">
            <h2>Children's Privacy</h2>
            <p>
                Our Service does not address anyone under 13. We do not knowingly collect personal information from children. Parents should contact Us if they believe we have collected such data.
            </p>
        </div>

        <!-- LINKS -->
        <div class="external-links">
            <h2>Links to Other Websites</h2>
            <p>
                Our Service may contain links to external websites. We are not responsible for their content, privacy policies, or practices.
            </p>
        </div>

        <!-- CHANGES -->
        <div class="policy-changes">
            <h2>Changes to this Privacy Policy</h2>
            <p>
                We may update this Privacy Policy periodically. Updates will be posted on this page and/or communicated via email or a notice on Our Service.
            </p>
        </div>

        <!-- CONTACT -->
        <div class="contact-us">
            <h2>Contact Us</h2>
            <p>If you have questions about this Privacy Policy, contact us:</p>
            <ul>
                <li>Email: arte@galleriastefanoforni.com</li>
            </ul>
        </div>
    </div>

</template>
<script>
export default {
    name: 'PrivacyPolicy',
    components: {

    }
}
</script>